<script context="module" lang="ts">
    export const TEST_ID = {
        ID: 'ellipse-id',
        LOCKUP: 'ellipse-lockup',
        CAPTION: 'ellipse-lockup__caption',
        TITLE: 'ellipse-lockup__title',
        SUB_TITLE: 'ellipse-lockup__sub-title',
    };
</script>

<script lang="ts">
    import Artwork from '@amp/web-app-components/src/components/Artwork/Artwork.svelte';
    import Monogram from '@amp/web-app-components/src/components/Monogram/Monogram.svelte';
    import type { Artwork as ArtworkType } from '@amp/web-app-components/src/components/Artwork/types';
    import LineClamp from '@amp/web-app-components/src/components/LineClamp/LineClamp.svelte';
    import PersonIcon from '@amp/web-app-components/assets/icons/person.fill.svg';
    import type { ComponentType } from 'svelte';

    const DEFAULT_USE_MONOGRAM_FN = () => false;
    const DEFAULT_GET_INITAL_FN = () => '';

    export let artwork: ArtworkType | 'slot';
    export let alt: string;
    export let caption: string | null = null;
    export let title: string | null;
    export let subtitles: string[] = [];
    export let useDefaultContainerStyles: boolean = true;
    export let displayText: boolean = true;
    export let customFallback: ComponentType | null = null;

    // Separately track whether to show the badge since its contents are conditional, but the slot itself isn't
    export let showBadge: boolean = true;

    export let shouldUseMonogram: () => boolean = DEFAULT_USE_MONOGRAM_FN;
    export let getMonogramInitals: () => string = DEFAULT_GET_INITAL_FN;

    let textContainer: HTMLDivElement;

    export function getTextContainer() {
        return textContainer;
    }
</script>

<div
    class="ellipse-lockup"
    data-testid={TEST_ID.LOCKUP}
    class:content-container={useDefaultContainerStyles}
>
    <div class="artwork" aria-hidden="true">
        {#if shouldUseMonogram() && getMonogramInitals()}
            <Monogram initials={getMonogramInitals()} />
        {:else}
            <slot
                name="artwork"
                {artwork}
                artworkProfile="ellipse-lockup"
                artworkAlt={alt}
            >
                {#if artwork !== 'slot'}
                    <Artwork {artwork} profile="ellipse-lockup" {alt}>
                        <svelte:fragment slot="placeholder-component">
                            <!-- Fallback -->
                            <div
                                class="artwork-fallback"
                                data-testid="artwork-fallback"
                            >
                                {#if customFallback}
                                    <svelte:component this={customFallback} />
                                {:else}
                                    <PersonIcon />
                                {/if}
                            </div>
                        </svelte:fragment>
                    </Artwork>
                {/if}
            </slot>
        {/if}
    </div>
    <div class="text-container" bind:this={textContainer}>
        {#if displayText}
            {#if caption}
                <span data-testid={TEST_ID.CAPTION} class="caption"
                    >{caption}</span
                >
            {/if}
            {#if title}
                <LineClamp
                    clamp={2}
                    allowFocusOverflow={true}
                    observe={true}
                    shouldRenderBadgeSlots={(showBadge &&
                        $$slots['favorited-badge']) ??
                        false}
                >
                    <h3 data-testid={TEST_ID.TITLE} class="title">
                        {title}
                    </h3>
                    <slot name="favorited-badge" slot="badge" />
                </LineClamp>
            {/if}
            {#if subtitles.length > 0}
                <p data-testid={TEST_ID.SUB_TITLE} class="subtitle">
                    {#each subtitles as subtitle}
                        <span class="subtitle-entry">{subtitle}</span>
                    {/each}
                </p>
            {/if}
        {/if}
    </div>
</div>

<style lang="scss">
    @use 'amp/stylekit/core/box-shadows' as *;
    @use 'amp/stylekit/core/mixins/hover-style' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;

    .content-container {
        .artwork {
            position: relative;
            // TODO: rdar://79348133 (Bring in copy + pasted variables into StyleKit)
            box-shadow: $global-default-content-box-shadow;

            &::after {
                // TODO: rdar://79348133 (Bring in copy + pasted variables into StyleKit)
                @include content-container-hover-style;
            }

            &::before {
                border-radius: 50%;
                box-shadow: inset 0 0 0 1px rgba(128, 128, 128, 0.1);
                content: '';
                display: block;
                height: 100%;
                max-width: 100%;
                max-height: 100%;
                pointer-events: none;
                position: absolute;
                top: 0;
                width: 100%;
                z-index: var(--z-default);
            }
        }

        .text-container {
            .title {
                display: inline;
            }
        }

        .text-container:hover {
            .title,
            .subtitle {
                text-decoration: underline;
            }
        }

        &:hover {
            .artwork::after {
                // TODO: rdar://79348133 (Bring in copy + pasted variables into StyleKit)
                @include content-container-hover-active-style;
            }
        }
    }

    .ellipse-lockup {
        text-align: center;

        // `div` needed for specificity override
        :global(.artwork),
        :global(div.artwork-component) {
            border-radius: 50%;
            overflow: hidden;
            z-index: var(--z-default);
        }

        .text-container:not(:has(.caption)) {
            margin-top: 8px;
        }

        .caption {
            color: var(--systemSecondary);
            font: var(--caption-1-emphasized);
        }

        .title {
            color: var(--systemPrimary);
            font: var(--callout-tall);

            @include line-clamp(2);
        }

        .subtitle {
            color: var(--systemSecondary);
            font: var(--callout);

            @include line-clamp(2);
        }

        .artwork-fallback {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            background-color: var(--genericJoeColor);

            :global(svg) {
                fill: #99999d;
            }
        }
    }

    .subtitle-entry:not(:empty) ~ .subtitle-entry:not(:empty)::before {
        content: ', ';
    }
</style>
